import {
  Component,
  h,
  Element,
  Prop,
  State,
  Watch,
  Method,
  Host,
} from "@stencil/core"
import uniqueId from "lodash-es/uniqueId"
import {
  StoreConnection,
  StateHandle,
  parseFunction,
  closest,
  dispatchEvent,
  getAppearance,
} from "../../core/helpers/webcomponent-helpers"
import {
  inputGroupReducer,
  getInitialState,
  InputGroupState,
  InputGroupActions,
} from "./input-group-store"
import { InputItemType, Theme } from "./types"

@Component({
  tag: "sdx-input-group",
  styleUrl: "input-group.scss",
  shadow: true,
})
export class InputGroup {
  private store: StoreConnection<this, InputGroupState, InputGroupActions>
  private invokeChangeCallback: Function = () => null
  private componentDidLoadComplete = false
  private isConnected = false
  private isReappending = false

  // When the selection changes by setting `this.value = [ "one", "two", ... ]`,
  // the checked item els will be updated - but syncing back to "this.value"
  // is necessary, as long as `this.value` is valid.
  // These flag control in which way the sync happens.
  private valueChangedInProgress = false
  private selectedInputItemElsChangedInProgress = false

  /**
   * Emitted whenever user is changing options.
   * @event
   */
  private static eventInput = "input"

  @Element() public el!: HTMLSdxInputGroupElement

  @State() public state!: StateHandle<InputGroupState>

  /**
   * The form input variant of the item.
   */
  @Prop() public type: InputItemType = "radio"

  /**
   * Styling theme for the input items within this group.
   */
  @Prop() public theme: Theme = "none"

  /**
   * Callback when an item is checked (or unchecked, when "checkbox").
   * @deprecated use input event instead.
   */
  @Prop() public changeCallback:
    | ((selection: any[]) => void)
    | string
    | undefined

  /**
   * Name parameter (useful when the component is used in a traditional HTML
   * form submit).
   */
  @Prop() public name?: string

  /**
   * Display all input items in a row (for maximum two items &mdash; more items
   * should be embedded in a grid).
   */
  @Prop() public inline: boolean = false

  /**
   * Label of the input group.
   */
  @Prop() public label: string = ""

  /**
   * The value(s) of the currently checked items(s). Can be used for both
   * getting and setting, like `myGroup.value = [ "value1" ]`.
   * Please note that this is always an array, even when `type="radio"`.
   * Please also note that when being used for setting the initial value, the
   * initial "checked" attribute on the `<sdx-input-item />` has the higher
   * priority.
   */
  @Prop({ mutable: true }) public value: any[] = []

  /**
   * Set this to false to declare the component as invalid and use the
   * `validation-message` attribute to explain why.
   */
  @Prop() public valid?: boolean

  /**
   * Text that explains the validation status to the user.
   */
  @Prop() public validationMessage?: string

  /**
   * Marks the component as required (please note that this itself does not
   * handle validation. Use the "valid" and "validation-message" attributes for
   * that).
   */
  @Prop() public required: boolean = false

  @Watch("state")
  public stateChanged({}, prev: StateHandle<InputGroupState>) {
    const state = this.state.get()
    const prevState = prev.get()

    if (state.selectedInputItemEls !== prevState.selectedInputItemEls) {
      this.selectedInputItemElsChanged(state)
    }
  }

  @Watch("type")
  public typeChanged() {
    this.store.set("type", this.type)
  }

  @Watch("changeCallback")
  public changeCallbackChanged() {
    this.setInvokeChangeCallback()
  }

  @Watch("name")
  public nameChanged() {
    this.store.set("name", this.name)
  }

  @Watch("inline")
  public inlineChanged() {
    this.store.set("inline", this.inline)
  }

  @Watch("theme")
  public themeChanged() {
    this.store.set("theme", this.theme)
  }

  @Watch("value")
  public valueChanged() {
    if (!this.componentDidLoadComplete) {
      return
    }

    this.valueChangedInProgress = true

    const { allValuesValid, validatedValues, inputItemEls } = this.getByValues(
      this.value
    )

    if (!allValuesValid) {
      this.value = validatedValues
      return
    }

    // Update the selection
    if (!this.selectedInputItemElsChangedInProgress) {
      // Set selection
      this.store.set("selectedInputItemEls", inputItemEls)
    }

    if (this.isConnected) {
      this.invokeChangeCallback(this.value)
    }

    this.valueChangedInProgress = false
  }

  /**
   * Returns the current selection.
   * @deprecated read the "value" prop instead
   */
  @Method()
  public async getSelection(): Promise<any[]> {
    return this.value
  }

  constructor() {
    this.name = uniqueId()

    this.setInvokeChangeCallback()

    this.store = new StoreConnection(
      this,
      inputGroupReducer,
      getInitialState(),
      ["selectedInputItemEls", "inputItemEls"]
    )

    this.store.set("type", this.type)
    this.store.set("theme", this.theme)
    this.store.set("inline", this.inline)
    this.store.set("name", this.name)

    this.store.flush()
  }

  public connectedCallback() {
    this.isConnected = true

    // The component is being reconnected
    if (this.isReappending) {
      this.store.set("reappending", true)

      this.isReappending = false
    }
  }

  public disconnectedCallback() {
    this.isConnected = false

    // Know that a disconnection happened, maybe because the component was
    // removed, cached or it's being appended to document.body.
    const isStillInDocument = closest(this.el, document.documentElement)

    if (isStillInDocument) {
      this.isReappending = true
    }
  }

  public componentDidLoad() {
    const { selectedInputItemEls } = this.state.get()

    // Initial selection:
    // If there items with the "checked" prop, use those.
    // If not, check if there's a "value" prop on the component and use that instead.
    if (selectedInputItemEls.length) {
      const { validatedValues } = this.getByValues(
        selectedInputItemEls.map((el) => el.value)
      )

      this.value = validatedValues
    } else {
      // use "value"
      const { allValuesValid, validatedValues, inputItemEls } =
        this.getByValues(this.value)

      if (allValuesValid && validatedValues.length) {
        this.store.set("selectedInputItemEls", inputItemEls)
      }
    }

    this.store.subscribe()

    this.componentDidLoadComplete = true
  }

  private selectedInputItemElsChanged(state: InputGroupState) {
    this.selectedInputItemElsChangedInProgress = true

    // Update the "value"
    if (!this.valueChangedInProgress) {
      const { validatedValues } = this.getByValues(
        state.selectedInputItemEls.map((el) => el.value)
      )
      this.value = validatedValues
    }

    this.selectedInputItemElsChangedInProgress = false
  }

  private setInvokeChangeCallback() {
    this.invokeChangeCallback = parseFunction(this.changeCallback)
  }

  /**
   * Checks if an array of values is valid and create a valid version of it.
   * For convenience, all input items matching the values will also be
   * returned.
   */
  private getByValues(values: any[]): {
    allValuesValid: boolean
    validatedValues: any[]
    inputItemEls: HTMLSdxInputItemElement[]
  } {
    const { inputItemEls } = this.state.get()

    if (Array.isArray(values)) {
      if (!values.length) {
        // Nothing to do, don't replace [] with [] because of change detection
        return {
          allValuesValid: true,
          validatedValues: values,
          inputItemEls: [],
        }
      }

      // Filter out undefined values
      const definedValues = values.filter((value) => value !== undefined)

      if (!definedValues.length) {
        // Either only undefined values or completely empty
        return { allValuesValid: false, validatedValues: [], inputItemEls: [] }
      }

      const validatedValues: any[] = []
      const foundInputItemEls: HTMLSdxInputItemElement[] = []
      let isValid = true

      // Check if values exist in the options
      for (let i = 0; i < definedValues.length; i++) {
        const value = definedValues[i]
        const foundInputItemEl = inputItemEls.find((el) => el.value === value)

        if (foundInputItemEl) {
          if (this.type === "checkbox" || (this.type === "radio" && i === 0)) {
            foundInputItemEls.push(foundInputItemEl)
            validatedValues.push(foundInputItemEl.value)
          } else {
            // Radios should only respect one value
            isValid = false
          }
        }
      }

      if (validatedValues.length === 0) {
        // no values found
        return {
          allValuesValid: false,
          validatedValues,
          inputItemEls: foundInputItemEls,
        }
      }

      return {
        allValuesValid: isValid,
        validatedValues,
        inputItemEls: foundInputItemEls,
      }
    }

    // All non-array types will be reset (to an empty array)
    return { allValuesValid: false, validatedValues: [], inputItemEls: [] }
  }

  private getComponentClassNames() {
    return {
      component: true,
      [getAppearance(this.el)]: true,
    }
  }

  private getLabelClassNames() {
    return {
      label: true,
      [getAppearance(this.el)]: true,
    }
  }

  private getSlotClassNames() {
    return {
      slot: true,
      inline: this.inline,
      "input-container": this.theme === "container",
    }
  }

  public render() {
    return (
      <Host role="group" aria-label={this.label}>
        <div class={this.getComponentClassNames()}>
          {this.label && (
            <label class={this.getLabelClassNames()}>
              {this.label}
              {this.required && <span aria-hidden="true">&nbsp;*</span>}
            </label>
          )}

          <div
            class={this.getSlotClassNames()}
            onInput={(e: Event) => {
              // stop input event of input-item and dispatch a new one where input-group is the new target
              e.stopPropagation()
              dispatchEvent(this.el, InputGroup.eventInput)
            }}
          >
            <slot />
          </div>

          {this.validationMessage && (
            <sdx-validation-message
              validationMessage={this.validationMessage}
            />
          )}
        </div>
      </Host>
    )
  }
}
